import { match } from 'ts-pattern'

import { BlendEventType, EventProperties, EventScope, EventSource, InternalEventType } from '../api/events/types'
import { emitBlendComplete, emitBlendEvent } from '../events/blend'
import { useStore } from '../stores/useStore'
import { useBlendOrderId } from './useBlendOrderId'
import { useMonitoring } from './useMonitoring'

export function useEventHandler() {
  const monitor = useMonitoring()
  const blendOrderId = useBlendOrderId()
  const hasLoggedIntoAtLeastOneEmployment = useStore(
    ({ hasLoggedIntoAtLeastOneEmployment }) => hasLoggedIntoAtLeastOneEmployment
  )

  return {
    handleEvent: (eventType: InternalEventType, properties?: EventProperties, callback?: () => void) => {
      return match(eventType)
        .with(InternalEventType.LOGIN_SUCCEEDED, () => {
          monitor({
            event_name: InternalEventType.LOGIN_SUCCEEDED,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
          emitBlendEvent({ eventType: BlendEventType.CONNECTED_ACCOUNT, orderId: blendOrderId })
        })
        .with(InternalEventType.LOGIN_FAILED, () => {
          monitor({
            event_name: InternalEventType.LOGIN_FAILED,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
          emitBlendEvent({ eventType: BlendEventType.LOGIN_ERROR, orderId: blendOrderId })
        })
        .with(InternalEventType.CONSENT_VIEWED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              scope: EventScope.SESSION,
            },
            ...properties,
          })
        })
        .with(InternalEventType.CONSENT_PROVIDED, () => {
          monitor({
            event_name: InternalEventType.CONSENT_PROVIDED,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              scope: EventScope.SESSION,
            },
            ...properties,
          })
        })
        .with(InternalEventType.ATTEMPTED_EXIT, () => {
          monitor({
            event_name: InternalEventType.ATTEMPTED_EXIT,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.CONFIRMED_EXIT, () => {
          monitor({
            event_name: InternalEventType.CONFIRMED_EXIT,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.CANCELLED_EXIT, () => {
          monitor({
            event_name: InternalEventType.CANCELLED_EXIT,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.FEEDBACK_SUBMITTED, () => {
          monitor({
            event_name: InternalEventType.FEEDBACK_SUBMITTED,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.RETURNED_FROM_FEEDBACK, () => {
          monitor({
            event_name: InternalEventType.RETURNED_FROM_FEEDBACK,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.CLICKED_VIDEO_LINK, () => {
          monitor({
            event_name: InternalEventType.CLICKED_VIDEO_LINK,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.EMPLOYER_SEARCH_VIEWED, () => {
          monitor({
            event_name: InternalEventType.EMPLOYER_SEARCH_VIEWED,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.PAYROLL_PLATFORM_SEARCH_VIEWED, () => {
          monitor({
            event_name: InternalEventType.PAYROLL_PLATFORM_SEARCH_VIEWED,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.CLICKED_SEARCH_RESULT, () => {
          monitor({
            event_name: InternalEventType.CLICKED_SEARCH_RESULT,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              ...properties?.internal_metadata,
            },
            ...properties,
          })
        })
        .with(InternalEventType.COMPLETED_EMPLOYER_VERIFICATION_FLOW, () => {
          monitor({
            event_name: InternalEventType.COMPLETED_EMPLOYER_VERIFICATION_FLOW,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.FAILED_EMPLOYER_VERIFICATION_FLOW, () => {
          monitor({
            event_name: InternalEventType.FAILED_EMPLOYER_VERIFICATION_FLOW,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.EDITED_EMPLOYER, () => {
          monitor({
            event_name: InternalEventType.EDITED_EMPLOYER,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.SHARED_EMPLOYER_DATA, () => {
          monitor({
            event_name: InternalEventType.SHARED_EMPLOYER_DATA,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              scope: EventScope.SESSION,
            },
            ...properties,
          })
          emitBlendComplete({ orderId: blendOrderId })
        })
        .with(InternalEventType.INCOME_TYPE_SELECTED, () => {
          monitor({
            event_name: InternalEventType.INCOME_TYPE_SELECTED,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.FILE_UPLOADED, () => {
          monitor({
            event_name: InternalEventType.FILE_UPLOADED,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.DOCUMENT_FLOW_COMPLETED, () => {
          monitor({
            event_name: InternalEventType.DOCUMENT_FLOW_COMPLETED,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.ADDED_EMPLOYER, () => {
          monitor({
            event_name: InternalEventType.ADDED_EMPLOYER,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.STARTED_EMPLOYER_VERIFICATION_FLOW, () => {
          monitor({
            event_name: InternalEventType.STARTED_EMPLOYER_VERIFICATION_FLOW,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              scope: EventScope.EMPLOYER,
              ...properties?.internal_metadata,
            },
            ...properties,
          })
        })
        .with(InternalEventType.EMPLOYER_HISTORY_VIEWED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              scope: EventScope.SESSION,
              ...properties?.internal_metadata,
            },
            ...properties,
          })
        })
        .with(InternalEventType.EMPLOYER_HISTORY_ADD_EMPLOYER, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              scope: EventScope.SESSION,
              ...properties?.internal_metadata,
            },
            ...properties,
          })
        })
        .with(InternalEventType.EXIT_VERIFICATION_VIEWED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              scope: EventScope.SESSION,
              ...properties?.internal_metadata,
            },
            ...properties,
          })
        })
        .with(InternalEventType.EXIT_VERIFICATION_CANCEL, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            internal_metadata: {
              scope: EventScope.SESSION,
              ...properties?.internal_metadata,
            },
            ...properties,
          })
        })
        .with(InternalEventType.EXIT_VERIFICATION_SUBMIT_FINISH_LATER, () => {
          monitor(
            {
              event_name: eventType,
              event_source: EventSource.INTERNAL_WIDGET,
              ...properties,
            },
            callback
          )
          if (hasLoggedIntoAtLeastOneEmployment) {
            emitBlendComplete({ orderId: blendOrderId })
          }
        })
        .with(InternalEventType.EXIT_VERIFICATION_SUBMIT_FINISH, () => {
          monitor(
            {
              event_name: eventType,
              event_source: EventSource.INTERNAL_WIDGET,
              ...properties,
            },
            callback
          )
          emitBlendComplete({ orderId: blendOrderId })
        })
        .with(InternalEventType.EXIT_VERIFICATION_SUBMIT_START_OVER, () => {
          monitor(
            {
              event_name: eventType,
              event_source: EventSource.INTERNAL_WIDGET,
              ...properties,
            },
            callback
          )
        })
        .with(InternalEventType.BOOT_PARTNER_WIDGET, () => {
          monitor(
            {
              event_name: eventType,
              event_source: EventSource.INTERNAL_WIDGET,
              internal_metadata: {
                scope: EventScope.SESSION,
                ...properties?.internal_metadata,
              },
              ...properties,
            },
            callback
          )
        })
        .with(InternalEventType.EMPLOYER_QUERIED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.PAYROLL_PLATFORM_QUERIED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.BANK_STATED_INFO_PROVIDED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.BANK_CONNECT_TIMEOUT, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.FINICITY,
            ...properties,
          })
        })
        .with(InternalEventType.BANK_WIDGET_FAILED_TO_LOAD, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.FINICITY,
            ...properties,
          })
        })
        .with(InternalEventType.BANK_INCOME_IDENTIFIED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.IRS_TASK_SELECTED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.IRS_TASK_SUBMITTED, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .with(InternalEventType.ATTEMPTED_TO_UPLOAD_UNSUPPORTED_IMAGE_TYPE, () => {
          monitor({
            event_name: eventType,
            event_source: EventSource.INTERNAL_WIDGET,
            ...properties,
          })
        })
        .exhaustive()
    },
  }
}
